// OPFS-persistent Munos KV store for a server hosted in a player's
// browser — the durable counterpart to the transient SqliteWasmMunosKv.
//
// The actual SQLite database runs in a dedicated Web Worker
// (opfs-sqlite-worker.ts), because the OPFS SAHPool VFS needs
// FileSystemSyncAccessHandle, which browsers expose only inside a worker.
// This class is the main-thread half: it spins up that worker and bridges
// each MunosKvBackend call across postMessage, resolving the returned
// promise when the worker replies. Genuinely async — which is exactly why
// the MunosKvBackend port is async; this backend and the networked
// libSQL/Turso one are the two that truly round-trip.
//
// Same SQLite dialect and schema as every other tier (managed libSQL,
// in-memory SqliteWasmMunosKv), so the cross-backend contract holds; the
// query bodies live in the worker and mirror SqliteWasmMunosKv exactly.

import { type KvWriteResult, type MunosKvBackend, DEFAULT_KV_MAX_KEYS } from "@multinostalgia/munos";

export type { KvWriteResult, MunosKvBackend } from "@multinostalgia/munos";

// ----- Admin/SQL query surface (used by the in-app database explorer) -----
//
// The KV ops above are the production port; the `query` op below is the
// escape hatch the on-demand DB admin panel uses to run arbitrary SQL
// against this exact persistent DB. BLOB cells never cross the worker
// boundary whole — they're summarised to a {byteLength, preview} descriptor
// so a `SELECT *` over megabyte values stays a cheap postMessage.

// A BLOB column value, summarised for display rather than shipped whole.
export interface BlobCell {
  readonly __blob: number; // total byte length of the value
  readonly preview: string; // first N bytes as printable text, else hex (truncated)
}

// One result cell. Mirrors SQLite's value domain, with BLOBs summarised.
export type CellValue = string | number | bigint | null | BlobCell;

// A single cell's FULL value, un-summarised — a BLOB arrives as the whole
// Uint8Array. Fetched on demand (one cell at a time) by the hex editor, so a
// large value is only shipped when the user actually opens it.
export type RawValue = Uint8Array | string | number | bigint | null;

export interface QueryResult {
  // Column names, in select order. Empty for statements that return no rows.
  readonly columns: string[];
  // Result rows (each a positional array aligned to `columns`).
  readonly rows: CellValue[][];
  // Rows changed by the statement (INSERT/UPDATE/DELETE); 0 for pure reads.
  readonly rowsAffected: number;
}

// Spec client-side default: 1 MiB per namespace (a browser host is still
// browser-resident storage).
const DEFAULT_QUOTA_BYTES = 1024 * 1024;

export interface OpfsSqliteMunosKvOptions {
  // Per-namespace quota in raw bytes. Default 1 MiB.
  quotaPerNamespace?: number;
  // Per-(scope, namespace) key-count ceiling. Defaults to DEFAULT_KV_MAX_KEYS.
  maxKeysPerNamespace?: number;
  // OPFS directory the SAHPool VFS owns. All files under it are managed by
  // the VFS. Distinct hosts/origins should use distinct names so their
  // stores stay invisible to each other. Default ".munos-host".
  opfsDirectory?: string;
}

interface Pending { resolve: (v: unknown) => void; reject: (e: Error) => void }
interface Reply { id: number; ok: boolean; result?: unknown; error?: string }

export class OpfsSqliteMunosKv implements MunosKvBackend {
  private seq = 0;
  private readonly pending = new Map<number, Pending>();

  private constructor(private readonly worker: Worker) {
    worker.addEventListener("message", (ev: MessageEvent) => {
      const msg = ev.data as Reply;
      const entry = this.pending.get(msg.id);
      if (!entry) return;
      this.pending.delete(msg.id);
      if (msg.ok) entry.resolve(msg.result);
      else entry.reject(new Error(msg.error ?? "opfs worker error"));
    });
  }

  // Spin up the worker, initialise the OPFS-backed DB, and return once the
  // schema is provisioned. Rejects if the browser can't run the OPFS VFS
  // (e.g. a non-worker-OPFS browser), so callers can fall back or report.
  static async create(opts: OpfsSqliteMunosKvOptions = {}): Promise<OpfsSqliteMunosKv> {
    const worker = new Worker(new URL("./opfs-sqlite-worker.ts", import.meta.url), {
      type: "module",
    });
    const kv = new OpfsSqliteMunosKv(worker);
    try {
      await kv.call("init", {
        quota: opts.quotaPerNamespace ?? DEFAULT_QUOTA_BYTES,
        maxKeys: opts.maxKeysPerNamespace ?? DEFAULT_KV_MAX_KEYS,
        directory: opts.opfsDirectory ?? ".munos-host",
      });
    } catch (e) {
      // init failed (e.g. a browser without worker-OPFS). Callers fall back,
      // so terminate the worker we just spawned rather than leaking the
      // thread and its SQLite-WASM module on every failed probe.
      worker.terminate();
      throw e;
    }
    return kv;
  }

  get(scope: string, ns: string, key: string): Promise<Uint8Array | undefined> {
    return this.call("get", { scope, ns, key }) as Promise<Uint8Array | undefined>;
  }

  set(scope: string, ns: string, key: string, data: Uint8Array): Promise<KvWriteResult> {
    return this.call("set", { scope, ns, key, data }) as Promise<KvWriteResult>;
  }

  del(scope: string, ns: string, key: string): Promise<void> {
    return this.call("del", { scope, ns, key }) as Promise<void>;
  }

  list(scope: string, ns: string, prefix: string, start = "", limit = 0, reverse = false): Promise<string[]> {
    return this.call("list", { scope, ns, prefix, start, limit, reverse }) as Promise<string[]>;
  }

  // Run arbitrary SQL against the persistent DB and return columns + rows.
  // Not part of MunosKvBackend — this powers the in-app DB admin panel. The
  // worker summarises BLOB values to keep the reply small. Rejects with the
  // SQLite error message on a malformed statement.
  query(sql: string, params?: readonly unknown[]): Promise<QueryResult> {
    return this.call("query", { sql, params: params ?? [] }) as Promise<QueryResult>;
  }

  // Fetch one cell's FULL value (the first column of the first row), with
  // BLOBs returned whole rather than summarised. Powers the hex editor:
  // `SELECT col FROM t WHERE <key>`. Resolves to null when no row matched.
  rawValue(sql: string, params?: readonly unknown[]): Promise<RawValue> {
    return this.call("rawValue", { sql, params: params ?? [] }) as Promise<RawValue>;
  }

  // Erase the host's OPFS storage (all files the VFS owns). Not part of
  // MunosKvBackend; the dev harness uses it to reset.
  wipe(): Promise<void> {
    return this.call("wipe", {}) as Promise<void>;
  }

  // Wipe the OPFS storage WITHOUT needing a working DB — spins a throwaway
  // worker that just clears the VFS. Lets a harness recover from a DB that
  // fails to open (e.g. one provisioned by a pre-migration build).
  static async wipe(opfsDirectory = ".munos-host"): Promise<void> {
    const worker = new Worker(new URL("./opfs-sqlite-worker.ts", import.meta.url), {
      type: "module",
    });
    const kv = new OpfsSqliteMunosKv(worker);
    try {
      await kv.call("wipe", { directory: opfsDirectory });
    } finally {
      kv.close();
    }
  }

  // Tear down the worker. Not part of MunosKvBackend; tests/harness call it.
  close(): void {
    this.worker.terminate();
    for (const { reject } of this.pending.values()) reject(new Error("backend closed"));
    this.pending.clear();
  }

  private call(op: string, args: Record<string, unknown>): Promise<unknown> {
    const id = ++this.seq;
    return new Promise<unknown>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ id, op, ...args });
    });
  }
}
